import { useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import type { PageState } from '@roll1/shared';
import { PanelRight } from 'lucide-react';
import { IconButton } from '../../ui/icon-button';
import { StudioMapCanvas } from '../StudioMapCanvas.js';
import { StudioMapMenu, type StudioMapControl } from '../StudioMapMenu.js';
import { usePageOpsContextOptional } from '../../../contexts/PageOpsContext.js';
import { useLayoutContextOptional } from '../../../contexts/LayoutContext.js';
import { useOptionalSessionContext } from '../../../contexts/SessionContext.js';
import { useI18n } from '../../../i18n/index.js';
import { pagePath } from '../../../lib/page-tree.js';

export interface PageEntityViewProps {
  pageId: string;
  pageName: string;
  tokenCount?: number;
  pageData?: PageState | null;
  draftPending?: boolean;
  onExitStudio: () => void;
  /** GM Studio map snapshots: create a snapshot of this location from its current state. */
  onSnapshot?: (label: string) => void;
  /** Sibling snapshots of the same location (includes this page). */
  snapshots?: readonly { id: string; name: string }[];
  /** Focus a sibling snapshot. */
  onFocusSnapshot?: (pageId: string) => void;
  /** Map → sheet (token) and map → journal entry (pin), opened in the Studio workspace. */
  onOpenSheet?: (sheetId: string) => void;
  onOpenJournalEntry?: (entryId: string) => void;
  /** Toolbar AI toggle → the Studio AI column. */
  aiOpen?: boolean;
  onToggleAi?: () => void;
  /** Open another page in the Studio workspace (a location link on this map, a crumb). */
  onOpenPage?: (pageId: string) => void;
  /** The map's image (upload / replace and where it stands), in the header of a live map. */
  imageControls?: ReactNode;
}

export function PageEntityView(props: PageEntityViewProps): JSX.Element {
  const { pageId, pageName, tokenCount, pageData, onExitStudio, onSnapshot, snapshots, onFocusSnapshot, onOpenSheet, onOpenJournalEntry, aiOpen, onToggleAi, onOpenPage, imageControls } = props;
  const pageOps = usePageOpsContextOptional();
  // The language the GM chose — the status strip beside these labels speaks it
  // too (a fixed DEFAULT_LOCALE mixed Russian labels into an English Studio).
  const { t } = useI18n();
  const session = useOptionalSessionContext();

  const resolvedTokenCount: number = tokenCount ?? 0;

  const layout = useLayoutContextOptional();
  // The map pane's own actions (clear drawings, AI map, drag the image) — filled by StudioMapCanvas.
  const controlRef = useRef<StudioMapControl | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);

  const handleOpenWorkshop = (): void => {
    // «Открыть за столом у себя»: выход из Studio с GM-превью этой карты,
    // не показ игрокам. PageOps живёт в AppShell и в Studio недоступен —
    // намерение оставляем в LayoutContext, его исполнит PageManager при
    // возврате к столу (мост, 2026-07-26). Прямой вызов — на случай, если
    // PageOps всё же смонтирован.
    if (pageOps !== null) {
      pageOps.pageCallbacks.preview(pageId);
    } else {
      layout?.dispatch({ type: 'layout:requestPreview', pageId });
    }
    onExitStudio();
  };

  // A live map fills its pane under one compact header line: this map vs the
  // players' map, and «⋯» with the map's own actions (package 5.4 — they used
  // to sit below a fixed-height canvas). The text-fallback card keeps the
  // centered layout under the same menu.
  const isMap = pageData !== null && pageData !== undefined;

  // Where the map sits in the tree of maps: «Мир › Регион › Карта». A top-level map has no crumbs.
  const path = session === null ? [] : pagePath(pageId, session.pages.pages);
  const ancestors = path.slice(0, -1);

  // The map's own actions, in a menu that portals above the panes (never
  // clipped by them, never over the canvas or its toolbar until opened).
  // «Snapshots of this location» are pages sharing its locationGroupId — not
  // the campaign's maps (those are in the Library) and not a beat's map.
  const menu = (
    <StudioMapMenu
      pageId={pageId}
      pageName={pageName}
      snapshots={snapshots}
      onSnapshot={onSnapshot}
      onFocusSnapshot={onFocusSnapshot}
      onOpenWorkshop={handleOpenWorkshop}
      controlRef={controlRef}
      isMap={isMap}
    />
  );

  return (
    <div
      className="page-entity-view flex min-h-0 w-full flex-1 flex-col"
      data-page-id={pageId}
    >
      {/* One 32 px line: where the map sits (its path) and the map's own icons. «У тебя / У игроков» is ONE line for the
          whole Studio (StudioMapStrip in the top bar, owner 30.10), not a strip over every map pane. */}
      <div
        className="page-entity-view__header pane-section-head @container flex flex-none items-center gap-x-1.5 border-b border-line bg-surface pr-1"
        data-testid="page-map-header"
      >
        {isMap ? (
          <nav aria-label={t('studio.mapCrumbs.label')} className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden px-2 text-meta text-muted-foreground">
            {ancestors.map((ancestor) => (
              <span key={ancestor.id} className="flex min-w-0 items-center gap-1">
                <button
                  type="button"
                  className="max-w-[10rem] truncate rounded px-1 hover:bg-panel-2 hover:text-fg"
                  title={ancestor.name}
                  onClick={(): void => onOpenPage?.(ancestor.id)}
                >
                  {ancestor.name}
                </button>
                <span aria-hidden="true">›</span>
              </span>
            ))}
            <span className="min-w-0 truncate font-medium text-fg" title={pageName} aria-current="page" data-testid="page-map-name">{pageName}</span>
          </nav>
        ) : (
          <span className="text-label font-semibold uppercase tracking-label text-dim page-entity-view__label min-w-0 flex-1 px-2 py-1.5">
            {t('studio.entity.cardPreview')}
          </span>
        )}
        <div className="ml-auto flex flex-none items-center gap-1">
        {isMap ? imageControls : null}
        {isMap ? (
          // An icon with its words in the hint (П3): the header stays one 32 px line in any pane.
          <IconButton
            label={t('studio.mapDrawer.open')}
            icon={<PanelRight size={14} aria-hidden="true" />}
            className={drawerOpen ? 'bg-hl/13 text-hl hover:text-hl' : undefined}
            aria-pressed={drawerOpen}
            data-testid="page-map-drawer-toggle"
            onClick={(): void => setDrawerOpen((open) => !open)}
          />
        ) : null}
        <div className="flex-none">{menu}</div>
        </div>
      </div>
      {isMap ? (
        <div className="page-entity-view__body flex min-h-0 w-full flex-1 flex-col p-1">
          {/* One instance per page (package 5.3): another page in this pane is
              a new working map; the old one hands its content back first. */}
          <StudioMapCanvas
            key={pageId}
            pageId={pageId}
            onOpenSheet={onOpenSheet}
            onOpenJournalEntry={onOpenJournalEntry}
            onOpenPage={onOpenPage}
            aiOpen={aiOpen}
            onToggleAi={onToggleAi}
            controlRef={controlRef}
            drawerOpen={drawerOpen}
          />
        </div>
      ) : (
        <div className="page-entity-view__body flex w-full flex-1 flex-col items-center justify-center gap-1 p-8 text-center">
          <h3 className="page-entity-view__name text-h1 font-semibold text-fg">{pageName}</h3>
          <p className="page-entity-view__tokens text-ui text-muted-foreground">
            {t('studio.entity.tokenCount').replace('{count}', String(resolvedTokenCount))}
          </p>
        </div>
      )}
    </div>
  );
}
